/**
 * LiveScorePreview — sample indicator that updates live as the user
 * adjusts a Mission sub-KPI's tolerance settings (DEV-816).
 *
 * Calls the registered Mission sub-KPI type's `score()` function with the
 * in-flight typeConfig and a synthetic data point. NOT duplicated math —
 * what the user previews here is exactly what the dashboard will render
 * once they save and data flows through.
 *
 * Usage in the wizard:
 *   <LiveScorePreview
 *     typeKey="count"
 *     typeConfig={state.typeConfig}
 *     displayName={state.displayName}
 *   />
 *
 * Renders:
 *   - Colored shape (triangle/circle) reflecting the indicator color
 *   - Display name
 *   - "X of Y" detail line (or type-specific equivalent)
 *   - Score chip with /100 suffix
 *   - "Try value" slider for sandbox-setting the current value
 *
 * The slider's range is derived from the type config (target × 1.2 for
 * counts, 0–100 for rates, etc.) so users can probe a meaningful range
 * without typing.
 */

import React, { useMemo, useState } from 'react';
import { getMissionSubKpiType } from '../../kpi-engine/missionTypes/registry';
import type { MissionDataPoint } from '../../kpi-engine/missionTypes/types';
import styles from './LiveScorePreview.module.css';

export interface LiveScorePreviewProps {
  /** Mission sub-KPI type key (binary | count | reduction | improvement | rate | milestone_set). */
  typeKey: string;
  /** Live typeConfig from the wizard state. */
  typeConfig: Record<string, unknown>;
  /** Display name to show in the preview card. Falls back to "Sample KPI". */
  displayName?: string;
}

/**
 * Default slider range per type. Falls back to 0–100 if the type doesn't
 * have a clear "current value" range from its config.
 */
function deriveSliderRange(
  typeKey: string,
  typeConfig: Record<string, unknown>,
): { min: number; max: number; step: number; defaultValue: number } {
  const c = typeConfig as any;
  switch (typeKey) {
    case 'binary':
      return { min: 0, max: 1, step: 1, defaultValue: 0 };
    case 'count': {
      const target = Number(c.target_value) || 100;
      // Allow probing 20% above target so users can see goal-met behavior.
      const max = Math.ceil(target * 1.2);
      return { min: 0, max, step: 1, defaultValue: Math.floor(target * 0.5) };
    }
    case 'reduction': {
      const baseline = Number(c.baseline_value) || 100;
      const target = Number(c.target_value) || 0;
      // For reduction, the value moves DOWN from baseline toward target.
      // Slider goes from target (best case) up to baseline (no progress).
      const lo = Math.min(baseline, target);
      const hi = Math.max(baseline, target);
      return { min: lo, max: hi, step: 1, defaultValue: Math.round((lo + hi) / 2) };
    }
    case 'improvement': {
      const baseline = Number(c.baseline_value) || 0;
      const target = Number(c.target_value) || 100;
      const max = Math.ceil(target * 1.1);
      return { min: baseline, max, step: 1, defaultValue: Math.round((baseline + target) / 2) };
    }
    case 'rate':
      return { min: 0, max: 100, step: 1, defaultValue: 50 };
    case 'milestone_set': {
      const total = Array.isArray(c.milestones) ? c.milestones.length : 0;
      return { min: 0, max: Math.max(total, 1), step: 1, defaultValue: Math.floor(total / 2) };
    }
    default:
      return { min: 0, max: 100, step: 1, defaultValue: 50 };
  }
}

/** Color band for the score (mirrors KPI_DEFAULTS.mission.bands defaults). */
function scoreToColorBand(score: number | null): 'grey' | 'red' | 'yellow' | 'green' {
  if (score === null || !Number.isFinite(score)) return 'grey';
  if (score >= 80) return 'green';
  if (score >= 60) return 'yellow';
  return 'red';
}

/** Map color band → CSS variable. */
function colorVar(band: 'grey' | 'red' | 'yellow' | 'green'): string {
  if (band === 'green') return 'var(--color-success)';
  if (band === 'yellow') return 'var(--color-warning)';
  if (band === 'red') return 'var(--color-error)';
  return 'var(--text-muted)';
}

/** SVG shape per band. Triangle up = green, circle = yellow/grey, triangle down = red. */
function ShapeSvg({ band }: { band: 'grey' | 'red' | 'yellow' | 'green' }) {
  const fill = colorVar(band);
  if (band === 'green') {
    return (
      <svg viewBox="0 0 28 28" className={styles.shapeSvg} aria-hidden="true">
        <polygon points="14,4 26,24 2,24" fill={fill} />
      </svg>
    );
  }
  if (band === 'red') {
    return (
      <svg viewBox="0 0 28 28" className={styles.shapeSvg} aria-hidden="true">
        <polygon points="2,4 26,4 14,24" fill={fill} />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 28 28" className={styles.shapeSvg} aria-hidden="true">
      <circle cx="14" cy="14" r="11" fill={fill} />
    </svg>
  );
}

export function LiveScorePreview({
  typeKey,
  typeConfig,
  displayName,
}: LiveScorePreviewProps) {
  const range = useMemo(
    () => deriveSliderRange(typeKey, typeConfig),
    [typeKey, typeConfig],
  );

  const [currentValue, setCurrentValue] = useState<number>(range.defaultValue);

  // Re-clamp when the range changes (e.g. user just changed target_value).
  // Keeps the slider thumb inside the valid range without losing the user's
  // intent (they were exploring at value=5 of target=6; if they bump
  // target to 10, slider should stay at 5, not jump).
  React.useEffect(() => {
    setCurrentValue((v) => {
      if (v < range.min) return range.min;
      if (v > range.max) return range.max;
      return v;
    });
  }, [range.min, range.max]);

  const scoreResult = useMemo(() => {
    const type = getMissionSubKpiType(typeKey);
    if (!type) return null;
    // Synthesize one data point at "today" for the registered scorer.
    // The score function takes (config, data, asOfDate) — pass today as the
    // asOf so deadline math reflects the current calendar.
    const today = new Date().toISOString().slice(0, 10);
    const data: MissionDataPoint[] = [{ date: today, value: currentValue }];
    try {
      return type.score(typeConfig as any, data, today);
    } catch (err) {
      // Bad config (e.g. validation fails mid-typing). Return null score
      // gracefully so the preview shows grey instead of crashing the wizard.
      if (typeof console !== 'undefined') {
        console.debug('[LiveScorePreview] score() threw', err);
      }
      return null;
    }
  }, [typeKey, typeConfig, currentValue]);

  const score = scoreResult?.score_0_100 ?? null;
  const band = scoreToColorBand(score);
  const target = scoreResult?.target_value;
  const explain = scoreResult?.explain ?? '';

  // Detail string varies by type. For count/milestone we show "X of Y".
  // For others the explain string from the engine already says it well.
  const detail = useMemo(() => {
    if (typeKey === 'count' || typeKey === 'milestone_set') {
      return target != null ? `${currentValue} of ${target}` : `${currentValue}`;
    }
    if (typeKey === 'binary') {
      return currentValue >= 1 ? 'Achieved' : 'Not yet';
    }
    return `${currentValue}${typeKey === 'rate' ? '%' : ''}`;
  }, [typeKey, currentValue, target]);

  return (
    <div className={styles.preview}>
      <div className={styles.indicator}>
        <ShapeSvg band={band} />
        <div className={styles.meta}>
          <div className={styles.name}>{displayName || 'Sample KPI'}</div>
          <div className={styles.detail}>{detail}</div>
        </div>
        <div className={styles.scoreCell}>
          <span className={styles.score}>{score == null ? '—' : Math.round(score)}</span>
          <span className={styles.scoreSuffix}>/100</span>
        </div>
      </div>

      {range.max > range.min && (
        <div className={styles.slider}>
          <label className={styles.sliderLabel} htmlFor="live-preview-slider">
            Try value
          </label>
          <input
            id="live-preview-slider"
            type="range"
            min={range.min}
            max={range.max}
            step={range.step}
            value={currentValue}
            onChange={(e) => setCurrentValue(Number(e.target.value))}
          />
          <span className={styles.sliderValue}>{currentValue}</span>
        </div>
      )}

      {explain && <div className={styles.explain}>{explain}</div>}
    </div>
  );
}
